import { useInfiniteQuery } from 'react-query';
import React, { useCallback, useRef, useState } from 'react';
import { TermsGlossaryEntry } from './TermsGlossaryEntry';
import { getSolrData } from '../../../hooks/useSolr';

export default function TermsGlossarySection({
    letter = [-1, '', ''],
    startAt = 0,
}) {
    // useInfiniteQuery code needs to be adjusted to do glossaries for other languages like Tibetan
    //const persp = usePerspective((state) => state.getPerspective('terms'));
    const persp = 'new.alpha';
    const [letterTid, letterName, letterHead] = letter;
    // console.log('start at', startAt);
    //const [entries, setEntries] = useState([]);
    const {
        data: sectionPages,
        error: isSectionError,
        fetchNextPage,
        hasNextPage,
        isFetching,
        isFetchingNextPage: isLoading,
    } = useInfiniteQuery(
        [letterTid.toString(), 'entries', persp, startAt], // QueryKey is used to pass parameters to entriesQuery() function
        entriesQuery,
        {
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            getNextPageParam: (lastPage, allPages) => lastPage?.nextPage,
            enabled: true,
        }
    );

    const observer = useRef();

    // Ref to div at end of list for infinitescrolling
    const lastItemRef = useCallback((node) => {
        // console.log("in use callback", node);
        if (isLoading) return;
        if (observer.current) observer.current.disconnect();

        observer.current = new IntersectionObserver((entries) => {
            //console.log('entries', entries.entries);
            if (entries[0].isIntersecting && hasNextPage) {
                // console.log("Fetched next page");
                fetchNextPage();
            }
        });
        if (node) {
            // console.log('setting observer');
            observer.current.observe(node);
        }
    });

    // Filter out the Terms grouping letters so they don't display
    const pages = sectionPages?.pages.map((pg, pid) => {
        const pgidat = pid === sectionPages.pages.length - 1 ? 'last' : pid;
        return (
            <div key={`gloss-pagekey-${pid}`} id={`gloss-page-${pgidat}`}>
                {pg.data
                    .filter((ent) => ent.associated_subjects[0] !== 'Letter')
                    .map((ent, ei) => (
                        <TermsGlossaryEntry
                            data={ent}
                            key={`gloss-entry-${ent.id}`}
                        />
                    ))}
            </div>
        );
    });

    const loading = isFetching || isLoading;
    const pagesLength =
        sectionPages && sectionPages?.pages ? sectionPages.pages.length : 0;
    const hasResults =
        pagesLength > 0 && sectionPages.pages[0].data?.length > 0;
    const showLoading =
        hasResults && sectionPages.pages[pagesLength - 1].nextPage;

    return (
        <div key={`container-letter-${letterTid}`}>
            <h2>
                Letter <span className="ne">{letterName}</span> ({letterHead})
            </h2>
            {!hasResults && !loading && (
                <div className="no-results">
                    <p>Sorry, there are no entries in this section.</p>
                </div>
            )}
            <div className="pl-5">{pages}</div>
            {showLoading && <div ref={lastItemRef}>Loading...</div>}
        </div>
    );
}

const entriesPerPage = 100;
// The actual async query function to retrieve the entries to display
const entriesQuery = async ({ pageParam = 0, queryKey }) => {
    // console.log('in entriesQuery', queryKey, 'pageParam', pageParam);
    const [ltid, label, perspective, startAt] = queryKey;
    const query = startAt
        ? `cascading_position_i:[${startAt} TO *] AND ancestor_ids_new.alpha:${ltid.replace(
              'terms-',
              ''
          )}`
        : `ancestor_ids_new.alpha:${ltid.replace('terms-', '')}`; //;
    const sectionQuery = {
        index: 'terms',
        params: {
            q: query,
            fq: [
                `block_type:parent`,
                `tree:terms`,
                '-associated_subject_ids:10524',
                `level_${perspective}_i:*`,
            ],
            start: pageParam * entriesPerPage,
            rows: entriesPerPage,
            fl: '*,[child parentFilter=block_type:parent limit=1000]',
            sort: `cascading_position_i ASC`,
        },
    };
    const resp = await getSolrData(sectionQuery, false);
    return {
        data: resp?.docs,
        nextPage:
            resp?.docs?.length === entriesPerPage ? pageParam + 1 : undefined,
    };
};
